import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp, DollarSign, Star, ShoppingBag } from "lucide-react";
import { apiFetch } from "@/lib/api";

/**
 * Phase 8B P0.3 — Aggregate dashboard row for AdminMarketplace.
 * Mounted at the top of the existing AdminMarketplace CRUD page.
 */
export default function MarketplaceDashboardCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try { setData(await apiFetch("/admin/marketplace/dashboard")); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) return (
        <div className="mb-6 py-8 text-center rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]">
            <Loader2 className="h-5 w-5 animate-spin mx-auto text-[var(--color-primary)]" />
        </div>
    );
    if (!data) return null;

    const kpi = [
        { label: "Revenue lifetime", value: `${data.revenue_credits_lifetime_total.toLocaleString()} c`, icon: DollarSign, testid: "mp-kpi-revenue-lifetime" },
        { label: "Revenue MTD", value: `${data.revenue_credits_mtd.toLocaleString()} c`, icon: TrendingUp, testid: "mp-kpi-revenue-mtd" },
        { label: "Top rated", value: `${data.top_by_rating.length}`, icon: Star, testid: "mp-kpi-top-rated" },
        { label: "Bundles", value: `${data.bundle_performance.length}`, icon: ShoppingBag, testid: "mp-kpi-bundles" },
    ];

    return (
        <div className="mb-6 space-y-4" data-testid="marketplace-dashboard">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {kpi.map((k) => (
                    <div key={k.label} data-testid={k.testid} className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                            <k.icon className="h-3 w-3" /> {k.label}
                        </div>
                        <div className="text-xl font-semibold text-text-main tabular-nums">{k.value}</div>
                    </div>
                ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                    <h3 className="text-sm font-semibold text-text-main mb-3">Top by revenue</h3>
                    {data.top_by_revenue.length === 0 ? <p className="text-xs text-text-secondary">No sales yet.</p> :
                    <ul className="text-xs space-y-1">
                        {data.top_by_revenue.map((r, i) => (
                            <li key={i} className="flex items-center justify-between border-b border-[var(--color-border)]/40 py-1">
                                <span className="text-text-main truncate max-w-[280px]">{r.title || r.asset_id}</span>
                                <span className="text-text-secondary tabular-nums">{r.revenue.toLocaleString()} c · {r.purchases}x</span>
                            </li>
                        ))}
                    </ul>}
                </div>
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
                    <h3 className="text-sm font-semibold text-text-main mb-3">Bundle performance</h3>
                    {data.bundle_performance.length === 0 ? <p className="text-xs text-text-secondary">No bundles yet.</p> :
                    <ul className="text-xs space-y-1">
                        {data.bundle_performance.map((r, i) => (
                            <li key={i} className="flex items-center justify-between border-b border-[var(--color-border)]/40 py-1">
                                <span className="text-text-main truncate max-w-[240px]">{r.title}</span>
                                <span className="text-text-secondary tabular-nums">{(r.discount_percentage || 0)}% off · {r.purchases}x</span>
                            </li>
                        ))}
                    </ul>}
                </div>
            </div>

            {data.low_performers.length > 0 && (
                <div className="rounded-2xl border border-neutral-400/30 bg-neutral-400/[0.03] p-5" data-testid="mp-low-performers">
                    <h3 className="text-sm font-semibold text-neutral-400 mb-3">Low performers · recommendations</h3>
                    <ul className="text-xs space-y-1">
                        {data.low_performers.map((r, i) => (
                            <li key={i} className="flex items-center justify-between border-b border-[var(--color-border)]/40 py-1">
                                <span className="text-text-main truncate max-w-[300px]">{r.title}</span>
                                <span className="text-text-secondary italic truncate max-w-[300px]">{r.recommendation}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
